import React, { useCallback, useContext } from 'react';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';

import { InventoryTabItemName } from '../../../../../InventoryStore';
import { useInventoryPermissions } from '../../../../../hooks/useInventoryPermissions';
import { useRowConfig } from '../../../../../hooks/useRowConfig';
import { getManualEditBadge } from '../../../../../utils/badges';
import type { RoomOpeningInventory } from '../../../../../utils/rooms';
import { RowBase } from '../../../../Table/RowBase/RowBase';
import type { InventoryValue } from '../../../inventoryStatusUtils';
import { getStatusInfo } from '../../../inventoryStatusUtils';

const RoomDiscountPriceRowComponent: React.FC<{ roomId: number }> = ({
  roomId,
}) => {
  const { inventoryRooms, onDuplicate, formatter } = useRowConfig({
    entityId: roomId,
    entityType: InventoryTabItemName.Rooms,
    fieldName: 'discountPrice',
    formatterName: 'discountPrice',
  });
  const { canEditDiscountPrice } = useInventoryPermissions();
  const { hotelInfo } = useContext(HotelContext);
  const { roomsList } = useContext(ModernHotelContext);
  const room = roomsList.find((r) => r.id === roomId);

  const handleGetBadge = useCallback(
    (values: InventoryValue) => {
      const roomValues = values as RoomOpeningInventory;

      return getManualEditBadge({
        stockScrapperEnabled: hotelInfo?.stockScrapperEnabled ?? false,
        bookingcomId: room?.bookingcomId,
        fieldName: 'discountPrice',
        discountMode: roomValues.discountMode,
        discountPrice: roomValues.discountPrice,
      });
    },
    [hotelInfo?.stockScrapperEnabled, room?.bookingcomId]
  );

  const handleGetStatusInfo = useCallback(
    (values: InventoryValue) => getStatusInfo(values as RoomOpeningInventory),
    []
  );

  if (!inventoryRooms) {
    return null;
  }

  return (
    <RowBase
      label="Room revenue share"
      hasInfoIcon
      tooltipTitle="Part of your package attributed to the room. For internal revenue use, not displayed to guests."
      entityId={roomId}
      fieldName="discountPrice"
      entityType={InventoryTabItemName.Rooms}
      getStatusInfo={handleGetStatusInfo}
      onDuplicate={onDuplicate}
      isEditable={canEditDiscountPrice}
      formatter={formatter}
      getBadge={handleGetBadge}
    />
  );
};

export const RoomDiscountPriceRow = React.memo(RoomDiscountPriceRowComponent);
